import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { browseCategoriesForDropdown, formatCount } from '../categoryLabels';
import { subcategorySlug } from '../categorySlug';
import { categoryBrowsePath, subcategoryBrowsePath } from '../categoryPaths';
import { trackSelectCategory, trackSelectSubcategory } from '../ga';
import './CategoryMenuDropdown.css';

function cx(...parts) {
  return parts.filter(Boolean).join(' ');
}

function categoryMenuAriaLabel(name, count) {
  const formatted = formatCount(count);
  if (!formatted) {
    return name;
  }
  return `${name}, ${formatted} products`;
}

function CategoryMenuCount({ count, className }) {
  const formatted = formatCount(count);
  if (!formatted) {
    return null;
  }
  return (
    <span className={cx('category-menu__count', className)} aria-hidden="true">
      {formatted}
    </span>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      className="category-menu__chevron"
      width="16"
      height="16"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M7.5 5L12.5 10L7.5 15"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CategorySubpanel({
  category,
  hrefForShop,
  hrefForSubcategory,
  onCategoryClick,
  onSubcategoryClick,
}) {
  const subcategories = Array.isArray(category?.subcategories)
    ? category.subcategories
    : [];

  if (!category) {
    return (
      <div className="category-menu__subpanel category-menu__subpanel--empty">
        <p className="category-menu__subpanel-hint">
          Choose a category to browse subcategories.
        </p>
      </div>
    );
  }

  return (
    <div className="category-menu__subpanel">
      <Link
        to={hrefForShop(category.slug)}
        className="category-menu__subpanel-all"
        aria-label={categoryMenuAriaLabel(`All in ${category.name}`, category.count)}
        onClick={() => onCategoryClick(category.slug)}
      >
        <span className="category-menu__subpanel-all-text">
          View all in {category.name}
          {formatCount(category.count) && (
            <>
              {' · '}
              <CategoryMenuCount
                count={category.count}
                className="category-menu__count--inline"
              />
            </>
          )}
        </span>
        <ChevronRightIcon />
      </Link>
      {subcategories.length > 0 ? (
        <ul className="category-menu__sub-list">
          {subcategories.map((sub) => (
            <li key={sub.name}>
              <Link
                to={hrefForSubcategory(sub, category.slug)}
                className="category-menu__sub-link"
                aria-label={categoryMenuAriaLabel(sub.name, sub.count)}
                onClick={() => onSubcategoryClick(category.slug, sub.name)}
              >
                <span className="category-menu__row-label">{sub.name}</span>
                <CategoryMenuCount count={sub.count} />
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="category-menu__subpanel-hint">
          Browse all products in this category.
        </p>
      )}
    </div>
  );
}

function DesktopCategoryMenu({
  dropdownCategories,
  activeShopCategorySlug,
  activeDepartmentSlug,
  onActiveDepartmentChange,
  hrefForShop,
  hrefForSubcategory,
  onCategoryClick,
  onSubcategoryClick,
}) {
  const activeDepartment =
    dropdownCategories.find((c) => c.slug === activeDepartmentSlug)
    ?? dropdownCategories[0]
    ?? null;

  return (
    <div className="category-menu__split">
      <div className="category-menu__departments">
        <ul className="category-menu__dept-list">
          {dropdownCategories.map((category) => {
            const isActive = activeDepartmentSlug === category.slug;
            const hasSubcategories =
              Array.isArray(category.subcategories)
              && category.subcategories.length > 0;

            return (
              <li key={category.slug}>
                <Link
                  to={hrefForShop(category.slug)}
                  className={cx(
                    'category-menu__dept-link',
                    isActive && 'category-menu__dept-link--focused',
                    activeShopCategorySlug === category.slug
                      && 'category-menu__dept-link--active',
                  )}
                  aria-label={categoryMenuAriaLabel(category.name, category.count)}
                  onMouseEnter={() => onActiveDepartmentChange(category.slug)}
                  onFocus={() => onActiveDepartmentChange(category.slug)}
                  onClick={() => onCategoryClick(category.slug)}
                >
                  <span className="category-menu__row-label">{category.name}</span>
                  <span className="category-menu__dept-meta">
                    <CategoryMenuCount count={category.count} />
                    {hasSubcategories && <ChevronRightIcon />}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
      <CategorySubpanel
        category={activeDepartment}
        hrefForShop={hrefForShop}
        hrefForSubcategory={hrefForSubcategory}
        onCategoryClick={onCategoryClick}
        onSubcategoryClick={onSubcategoryClick}
      />
    </div>
  );
}

function MobileDepartmentsStep({
  dropdownCategories,
  activeShopCategorySlug,
  hrefForShop,
  onOpenDepartment,
  onCategoryClick,
}) {
  return (
    <ul className="category-menu__mobile-list">
        {dropdownCategories.map((category) => {
          const hasSubcategories =
            Array.isArray(category.subcategories)
            && category.subcategories.length > 0;

          if (hasSubcategories) {
            return (
              <li key={category.slug}>
                <button
                  type="button"
                  className={cx(
                    'category-menu__mobile-row',
                    activeShopCategorySlug === category.slug
                      && 'category-menu__mobile-row--active',
                  )}
                  aria-label={categoryMenuAriaLabel(category.name, category.count)}
                  onClick={() => onOpenDepartment(category.slug)}
                >
                  <span className="category-menu__row-label">{category.name}</span>
                  <span className="category-menu__dept-meta">
                    <CategoryMenuCount count={category.count} />
                    <ChevronRightIcon />
                  </span>
                </button>
              </li>
            );
          }

          return (
            <li key={category.slug}>
              <Link
                to={hrefForShop(category.slug)}
                className={cx(
                  'category-menu__mobile-row',
                  'category-menu__mobile-row--link',
                  activeShopCategorySlug === category.slug
                    && 'category-menu__mobile-row--active',
                )}
                aria-label={categoryMenuAriaLabel(category.name, category.count)}
                onClick={() => onCategoryClick(category.slug)}
              >
                <span className="category-menu__row-label">{category.name}</span>
                <CategoryMenuCount count={category.count} />
              </Link>
            </li>
          );
        })}
    </ul>
  );
}

function MobileSubcategoriesStep({
  category,
  hrefForShop,
  hrefForSubcategory,
  onCategoryClick,
  onSubcategoryClick,
}) {
  const subcategories = Array.isArray(category?.subcategories)
    ? category.subcategories
    : [];

  return (
    <>
      <Link
        to={hrefForShop(category.slug)}
        className="category-menu__mobile-link category-menu__mobile-link--all"
        aria-label={categoryMenuAriaLabel(`All in ${category.name}`, category.count)}
        onClick={() => onCategoryClick(category.slug)}
      >
        <span className="category-menu__row-label">
          All in {category.name}
          {formatCount(category.count) && (
            <>
              {' · '}
              <CategoryMenuCount
                count={category.count}
                className="category-menu__count--inline"
              />
            </>
          )}
        </span>
      </Link>
      <ul className="category-menu__mobile-list">
        {subcategories.map((sub) => (
          <li key={sub.name}>
            <Link
              to={hrefForSubcategory(sub, category.slug)}
              className="category-menu__mobile-row category-menu__mobile-row--link"
              aria-label={categoryMenuAriaLabel(sub.name, sub.count)}
              onClick={() => onSubcategoryClick(category.slug, sub.name)}
            >
              <span className="category-menu__row-label">{sub.name}</span>
              <CategoryMenuCount count={sub.count} />
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}

export function CategoryMenuDropdown({
  browseCategories,
  activeShopCategorySlug,
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const [activeDepartmentSlug, setActiveDepartmentSlug] = useState(null);
  const [mobileStep, setMobileStep] = useState('departments');
  const [mobileDepartmentSlug, setMobileDepartmentSlug] = useState(null);
  const [isMobileModal, setIsMobileModal] = useState(false);
  const rootRef = useRef(null);

  const dropdownCategories = useMemo(
    () => browseCategoriesForDropdown(browseCategories, activeShopCategorySlug),
    [browseCategories, activeShopCategorySlug],
  );

  const mobileDepartment = useMemo(
    () => dropdownCategories.find((c) => c.slug === mobileDepartmentSlug) ?? null,
    [dropdownCategories, mobileDepartmentSlug],
  );

  function hrefForShop(slug) {
    return categoryBrowsePath(slug);
  }

  function hrefForSubcategory(sub, shopSlug) {
    return subcategoryBrowsePath(shopSlug, subcategorySlug(sub));
  }

  function closeMenu() {
    setOpen(false);
  }

  function handleCategoryClick(slug) {
    trackSelectCategory(slug, 'category_menu');
    closeMenu();
  }

  function handleSubcategoryClick(categorySlug, subcategoryName) {
    trackSelectSubcategory(categorySlug, subcategoryName, 'category_menu');
    closeMenu();
  }

  function openMobileDepartment(slug) {
    setMobileDepartmentSlug(slug);
    setMobileStep('subcategories');
  }

  function backToMobileDepartments() {
    setMobileStep('departments');
    setMobileDepartmentSlug(null);
  }

  useEffect(() => {
    const mobileQuery = window.matchMedia('(max-width: 768px)');
    const sync = () => setIsMobileModal(mobileQuery.matches);
    sync();
    mobileQuery.addEventListener('change', sync);
    return () => mobileQuery.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!open || !isMobileModal) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileModal, open]);

  useEffect(() => {
    function handlePointerDown(e) {
      if (!rootRef.current?.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, []);

  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') {
        if (isMobileModal && mobileStep === 'subcategories') {
          backToMobileDepartments();
          return;
        }
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('keydown', handleKey);
      return () => document.removeEventListener('keydown', handleKey);
    }
    return undefined;
  }, [isMobileModal, mobileStep, open]);

  useEffect(() => {
    if (!open) {
      setActiveDepartmentSlug(null);
      setMobileStep('departments');
      setMobileDepartmentSlug(null);
      return;
    }

    const defaultSlug =
      activeShopCategorySlug
      || dropdownCategories[0]?.slug
      || null;
    setActiveDepartmentSlug(defaultSlug);
    setMobileStep('departments');
    setMobileDepartmentSlug(null);
  }, [open, activeShopCategorySlug, dropdownCategories]);

  const modalTitle =
    isMobileModal && mobileStep === 'subcategories' && mobileDepartment
      ? mobileDepartment.name
      : 'Categories';

  return (
    <div className="category-menu" ref={rootRef}>
      <button
        type="button"
        className="category-menu__trigger"
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls="category-menu-panel"
        id="category-menu-button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="category-menu__trigger-text">Categories</span>
      </button>
      {open && (
        <>
          <button
            type="button"
            className="category-menu__backdrop"
            aria-hidden="true"
            tabIndex={-1}
            onClick={closeMenu}
          />
          <div
            className="category-menu__panel"
            id="category-menu-panel"
            role={isMobileModal ? 'dialog' : 'region'}
            aria-modal={isMobileModal ? 'true' : undefined}
            aria-labelledby={
              isMobileModal ? 'category-menu-title' : 'category-menu-button'
            }
          >
            <div className="category-menu__modal-header">
              {isMobileModal && mobileStep === 'subcategories' ? (
                <button
                  type="button"
                  className="category-menu__back"
                  onClick={backToMobileDepartments}
                >
                  <span aria-hidden="true">←</span>
                  Categories
                </button>
              ) : null}
              <h2
                id="category-menu-title"
                className={cx(
                  'category-menu__modal-title',
                  isMobileModal
                    && mobileStep === 'subcategories'
                    && 'category-menu__modal-title--with-back',
                )}
              >
                {modalTitle}
              </h2>
              <button
                type="button"
                className="category-menu__close"
                aria-label="Close categories"
                onClick={closeMenu}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <nav className="category-menu__nav" aria-label="Product categories">
              {isMobileModal ? (
                mobileStep === 'departments' ? (
                  <MobileDepartmentsStep
                    dropdownCategories={dropdownCategories}
                    activeShopCategorySlug={activeShopCategorySlug}
                    hrefForShop={hrefForShop}
                    onOpenDepartment={openMobileDepartment}
                    onCategoryClick={handleCategoryClick}
                  />
                ) : (
                  <MobileSubcategoriesStep
                    category={mobileDepartment}
                    hrefForShop={hrefForShop}
                    hrefForSubcategory={hrefForSubcategory}
                    onCategoryClick={handleCategoryClick}
                    onSubcategoryClick={handleSubcategoryClick}
                  />
                )
              ) : (
                <DesktopCategoryMenu
                  dropdownCategories={dropdownCategories}
                  activeShopCategorySlug={activeShopCategorySlug}
                  activeDepartmentSlug={activeDepartmentSlug}
                  onActiveDepartmentChange={setActiveDepartmentSlug}
                  hrefForShop={hrefForShop}
                  hrefForSubcategory={hrefForSubcategory}
                  onCategoryClick={handleCategoryClick}
                  onSubcategoryClick={handleSubcategoryClick}
                />
              )}
            </nav>
          </div>
        </>
      )}
    </div>
  );
}
